import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row } from "react-bootstrap";
import { ToastContainer } from 'react-toastify';
import { useNavigate, useSearchParams } from "react-router-dom";
import { showToast } from '../reusables/CommonFunction';
import axiosInstance from '../../utils/axios';
import BusyLoaderOverlay from '../reusables/BusyLoaderOverlay';

export default function AccountActivate() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchParams] = useSearchParams();
    const [activateState, setActivateState] = useState({
        password: "",
        confirm_password: "",
    });
    const [activateErrors, setActivateErrors] = useState({
        password: "",
        confirm_password: "",
    });
    const [responseState, setResponseState] = useState({
        busyIndicator: false,
        message: ''
    });

    const uid = searchParams.get("uid");
    const token = searchParams.get("token");
    const type = searchParams.get("type");

    useEffect(() => {
        if (!uid || !token) {
            showToast("error", "Invalid activation link. Please contact support.");
            navigate("/sign-in", { replace: true });
        }
    }, [uid, token, navigate]);

    const onChange = (e) => {
        setActivateState({
            ...activateState,
            [e.target.name]: e.target.value,
        });
        if (activateErrors[e.target.name]) {
            setActivateErrors({
                ...activateErrors,
                [e.target.name]: "",
            });
        }
    };

    const onPressAccountActivate = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        let hasError = false;

        const passwordRules = [
            { regex: /.{8,}/, message: "Password length should be a minimum of 8 characters." },
            { regex: /(?=.*[A-Z])/, message: "Password must have at least one Uppercase character." },
            { regex: /(?=.*[a-z])/, message: "Password must have at least one Lowercase character." },
            { regex: /(?=.*[0-9])/, message: "Password must have at least one Digit." },
            { regex: /(?=.*[#?!@$%^&*-])/, message: "Password must have at least one Special Character." },
        ];

        // Validate password
        if (!activateState.password.trim()) {
            hasError = true;
            setActivateErrors(prev => ({ ...prev, password: "Password should not be empty." }));
        } else {
            for (const rule of passwordRules) {
                if (!rule.regex.test(activateState.password)) {
                    hasError = true;
                    setActivateErrors(prev => ({ ...prev, password: rule.message }));
                    break;
                }
            }
        }

        // Validate confirm password
        if (!activateState.confirm_password.trim()) {
            hasError = true;
            setActivateErrors(prev => ({ ...prev, confirm_password: "Confirm Password should not be empty." }));
        } else if (activateState.password !== activateState.confirm_password) {
            hasError = true;
            setActivateErrors(prev => ({ ...prev, confirm_password: "Passwords do not match." }));
        }

        if (hasError) {
            setIsSubmitting(false);
            return;
        }

        const payload = {
            uid,
            token,
            type,
            password: activateState.password
        };

        try {
            setLoading(true);
            setResponseState(prev => ({ ...prev, busyIndicator: true }));
            const response = await axiosInstance.post("/user/account/activate/", payload, { skipAuth: true });
            setResponseState(prev => ({ ...prev, busyIndicator: false, message: response.data.message }));

            if (response.status === 202) {
                showToast("success", response.data.message, {
                    onClose: () => navigate("/sign-in", { replace: true })
                });
            } else {
                showToast("error", response.data.message);
            }
        } catch (error) {
            showToast("error", error.response?.data?.message || "Error during account activation.");
        } finally {
            setLoading(false);
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Container className="ml-3 mr-3 mb-3">
                <Row>
                    <div className="col-md-12">
                        <div className="content-section col-md-8">
                            <legend className="border-bottom mb-4">Activate Your Account</legend>
                            <Form autoComplete="off" noValidate onSubmit={onPressAccountActivate}>
                                <Form.Group className="mb-3" controlId="formPassword">
                                    <Form.Label>New Password</Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="password"
                                        value={activateState.password}
                                        onChange={onChange}
                                        isInvalid={!!activateErrors.password}
                                    />
                                    <Form.Text muted>Password must contain at least one uppercase.</Form.Text>
                                    <Form.Text muted>Password must contain at least one lowercase.</Form.Text>
                                    <Form.Text muted>Password must contain at least one digit.</Form.Text>
                                    <Form.Text muted>Password must contain at least one special character.</Form.Text>
                                    <Form.Text muted>Password should be of minimum 8 characters.</Form.Text>
                                    <Form.Control.Feedback type="invalid">{activateErrors.password}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="formConfirmPassword">
                                    <Form.Label>Confirm Password</Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="confirm_password"
                                        value={activateState.confirm_password}
                                        onChange={onChange}
                                        isInvalid={!!activateErrors.confirm_password}
                                    />
                                    <Form.Text muted>Enter the same password as before, for verification.</Form.Text>
                                    <Form.Control.Feedback type="invalid">{activateErrors.confirm_password}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Button type="submit" variant="primary" className="form-control" disabled={isSubmitting}>
                                        {isSubmitting ? 'Activating...' : 'Activate Account'}
                                    </Button>
                                </Form.Group>
                            </Form>
                        </div>
                    </div>
                </Row>
                <ToastContainer />
                <BusyLoaderOverlay show={loading} />
            </Container>
        </>
    );
}
